import { computed, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { Message } from '@arco-design/web-vue'
import { listEnumItemsByTypeCode, type SysEnumOptionVO } from '@/api/sys/enum'
import { SYS_ENUM_DEFAULT_CHANNEL, SYS_ENUM_TYPE } from '@/constants/sys-enum-type'

const items = ref<SysEnumOptionVO[]>([])
const loading = ref(false)
let loaded = false
let loadingPromise: Promise<void> | null = null

const loadSpaceStatuses = (force = false): Promise<void> => {
  if (loadingPromise) return loadingPromise
  if (loaded && !force) return Promise.resolve()

  loading.value = true
  loadingPromise = listEnumItemsByTypeCode(SYS_ENUM_TYPE.SPACE_STATUS, SYS_ENUM_DEFAULT_CHANNEL)
    .then(list => {
      items.value = list || []
      // 未配置时允许后续页面重新获取，方便平台补录后生效。
      loaded = items.value.length > 0
    })
    .finally(() => {
      loading.value = false
      loadingPromise = null
    })
  return loadingPromise
}

/** 海运整箱、空运共用平台舱位状态；适用业务由 businessTypes 扩展值维护。 */
export function useSpaceStatusOptions(businessType?: 'fcl' | 'air') {
  const { t, locale } = useI18n()
  const allSpaceStatusOptions = computed(() => items.value
    .filter(item => item.itemCode?.trim())
    .map(item => ({
      value: Number(item.itemCode),
      label: locale.value.startsWith('zh')
        ? (item.itemValue || item.enName || String(item.itemCode))
        : (item.enName || item.itemValue || String(item.itemCode)),
      sortOrder: item.sortOrder ?? Number.MAX_SAFE_INTEGER
    }))
    .filter(item => Number.isSafeInteger(item.value) && item.value > 0)
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map(({ value, label }) => ({ value, label })))

  const spaceStatusOptions = computed(() => {
    if (!businessType) return allSpaceStatusOptions.value
    const applicableCodes = new Set(items.value
      .filter(item => item.extensionData?.some(extension =>
        extension.extensionKey?.trim() === 'businessTypes'
        && extension.extensionValue?.split(',').some(value => value.trim().toLowerCase() === businessType)
      ))
      .map(item => Number(item.itemCode)))
    return allSpaceStatusOptions.value.filter(option => applicableCodes.has(option.value))
  })

  const getSpaceStatusLabel = (value?: number | string | null): string => {
    if (value === null || value === undefined || String(value).trim() === '') return '-'
    return allSpaceStatusOptions.value.find(option => option.value === Number(value))?.label || String(value)
  }

  const getSpaceStatusFallbackOption = (value: string | number | boolean | Record<string, unknown>) => ({
    value,
    label: getSpaceStatusLabel(String(value)),
    disabled: true
  })

  onMounted(() => {
    loadSpaceStatuses().catch(error => {
      console.error('Failed to load platform space statuses', error)
      Message.error(t('common.loadFailed'))
    })
  })

  return { spaceStatusOptions, getSpaceStatusLabel, getSpaceStatusFallbackOption, loadSpaceStatuses, loading }
}
